import { memo } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FileText, File, Eye } from "lucide-react";
import { truncateFileName } from "../../preOnboarding/preview/UploadedFilesSection";

const isFileObject = (file) => {
  return file && typeof file === "object" && "size" in file && "type" in file;
};

const isStringUrl = (file) => {
  return typeof file === "string";
};

const DocumentPreview = memo(({ file, fileName, onPreview }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!file) return null;

  const isFile = isFileObject(file);
  const isUrl = isStringUrl(file);

  const isImage =
    isFile && file.type.startsWith("image/")
      ? true
      : isUrl && file.match(/\.(jpeg|jpg|png|webp|gif)$/i)
      ? true
      : false;

  const isPDF =
    isFile && file.type === "application/pdf"
      ? true
      : isUrl && file.toLowerCase().endsWith(".pdf")
      ? true
      : false;

  const fileSizeMB =
    isFile && file.size ? (file.size / 1024 / 1024).toFixed(2) : "--";

  const displayFileName = fileName || "document";

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "white",
        borderRadius: "8px",
        border: "1px solid #d1fae5",
        display: "flex",
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "center" },
        flexDirection: { xs: "column", sm: "row" },
        gap: 2,
        mb: 1.5,
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: "#f0fdf4",
          borderColor: "#86efac",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1, minWidth: 0, width: { xs: "100%", sm: "auto" } }}>
        <Box
          sx={{
            minWidth: "40px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {isImage ? (
            <Box
              component="img"
              src={isFile ? URL.createObjectURL(file) : file}
              sx={{
                width: "35px",
                height: "35px",
                borderRadius: "4px",
                objectFit: "cover",
                border: "1px solid #e5e7eb",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
              alt={displayFileName}
            />
          ) : isPDF ? (
            <FileText size={32} style={{ color: "#dc2626" }} />
          ) : (
            <File size={32} style={{ color: "#2563eb" }} />
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: "#1f2937",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: "0.9rem",
            }}
            title={displayFileName}
          >
            {isMobile
              ? truncateFileName(displayFileName, 20)
              : truncateFileName(displayFileName, 30)}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.5 }}>
            {fileSizeMB !== "--" && (
              <Typography variant="caption" sx={{ color: "#6b7280" }}>
                {fileSizeMB} MB
              </Typography>
            )}
            <Chip
              label={isImage ? "Image" : isPDF ? "PDF" : "File"}
              size="small"
              sx={{
                backgroundColor: isImage
                  ? "#dbeafe"
                  : isPDF
                  ? "#fee2e2"
                  : "#f3f4f6",
                color: isImage ? "#0369a1" : isPDF ? "#dc2626" : "#6b7280",
                height: "20px",
                fontSize: "11px",
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          gap: 1,
        }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<Eye size={16} />}
          onClick={() => onPreview(file, displayFileName)}
          sx={{ minHeight: 35 }}
        >
          {isUrl ? "View" : "Preview"}
        </Button>
      </Box>
    </Box>
  );
});

export default DocumentPreview;
